import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { getMonthDays } from "../utils/month";
import getWorkcellColor from "../utils/getWorkcellColor";
import { parseLocalDateTime } from "../utils/localDateTime";

const PALETTE = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#d8b416",
  "#7592dd",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#003f5c",
  "#ffa600",
];

const MonthlyWorkcellOeeScheduler = ({ oeeData, date }) => {
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const [activeDataKeys, setActiveDataKeys] = useState(() => new Set());
  const [hiddenDataKeys, setHiddenDataKeys] = useState(() => new Set());

  const formatPercent01 = (value01) => {
    const num = Number(value01);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(num);
  };

  const { chartData, workcells, workcellIds } = useMemo(() => {
    const days = getMonthDays(date);
    const rowsByDate = new Map(days.map((day) => [day, { day }]));
    const workcellMap = new Map();

    oeeData.forEach((entry) => {
      const dayRow = rowsByDate.get(entry.date);
      if (!dayRow) return;

      const id = String(entry.workcell_id);
      const name = entry.workcell_name || `workcell_${id}`;

      if (!workcellMap.has(id)) {
        workcellMap.set(id, {
          id,
          name,
          dataKey: `wc_${id}`,
        });
      }

      const series = workcellMap.get(id);
      dayRow[series.dataKey] = entry.oee;
      dayRow[`${series.dataKey}__target`] = entry.target_oee;
      dayRow[`${series.dataKey}__actual`] = entry.actual_hours;
      dayRow[`${series.dataKey}__scheduled`] = entry.scheduled_hours;
    });

    const sortedWorkcells = Array.from(workcellMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, locale, { numeric: true, sensitivity: "base" })
    );

    return {
      chartData: days.map((day) => rowsByDate.get(day)),
      workcells: sortedWorkcells,
      workcellIds: sortedWorkcells.map((workcell) => workcell.id),
    };
  }, [date, locale, oeeData]);

  const legendRows = useMemo(() => (
    workcells.map((workcell) => {
      const values = chartData
        .map((row) => Number(row[workcell.dataKey]))
        .filter((value) => Number.isFinite(value) && value > 0);

      const min = values.length ? Math.min(...values) : null;
      const max = values.length ? Math.max(...values) : null;
      const avg = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

      return {
        ...workcell,
        min,
        max,
        avg,
      };
    })
  ), [chartData, workcells]);

  const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload?.length) return null;

    const visibleItems = payload
      .filter((item) => item?.dataKey && !String(item.dataKey).includes("__") && item.value != null)
      .sort((a, b) => {
        const aValue = Number(a.value);
        const bValue = Number(b.value);
        return (Number.isFinite(bValue) ? bValue : -1) - (Number.isFinite(aValue) ? aValue : -1);
      });

    if (!visibleItems.length) return null;

    return (
      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {parseLocalDateTime(label)?.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) || label}
        </Typography>
        {visibleItems.map((item) => {
          const actual = item.payload?.[`${item.dataKey}__actual`];
          const scheduled = item.payload?.[`${item.dataKey}__scheduled`];
          const target = item.payload?.[`${item.dataKey}__target`];

          return (
            <Box key={item.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
              <Typography variant="body2" component="div">
                {item.name}: {formatPercent01(item.value)}
                {Number.isFinite(target) ? ` (${t("monthly.oeeScheduler.chart.target")}: ${formatPercent01(target)})` : ""}
                {Number.isFinite(actual) && Number.isFinite(scheduled)
                  ? ` (${t("monthly.oeeScheduler.chart.actual")}: ${actual.toFixed(1)}h, ${t("monthly.oeeScheduler.chart.scheduled")}: ${scheduled.toFixed(1)}h)`
                  : ""}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleLegendClick = (dataKey) => {
    setActiveDataKeys((current) => {
      const next = new Set(current);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  const handleLegendDoubleClick = (dataKey) => {
    setHiddenDataKeys((current) => {
      const next = new Set(current);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
    setActiveDataKeys((current) => {
      const next = new Set(current);
      next.delete(dataKey);
      return next;
    });
  };

  if (!workcells.length) {
    return (
      <Box
        sx={{
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography>{t("monthly.oeeScheduler.chart.noData")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: 520 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
              minTickGap={12}
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(value) => `${Math.round(value * 100)}%`}
              label={{ value: t("monthly.oeeScheduler.chart.axisLabel"), angle: -90, position: "insideLeft" }}
            />
            <Tooltip content={<CustomTooltip />} />
            {workcells.map((workcell) => (
              <React.Fragment key={workcell.dataKey}>
                <Line
                  type="monotone"
                  dataKey={`${workcell.dataKey}__target`}
                  name={`${workcell.name} target`}
                  stroke={getWorkcellColor(workcell.id, PALETTE, workcellIds)}
                  strokeWidth={activeDataKeys.has(workcell.dataKey) ? 2.5 : 1.5}
                  strokeDasharray="6 4"
                  dot={false}
                  activeDot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                  hide={hiddenDataKeys.has(workcell.dataKey)}
                  legendType="none"
                  opacity={activeDataKeys.size === 0 || activeDataKeys.has(workcell.dataKey) ? 0.9 : 0.2}
                />
                <Line
                  type="monotone"
                  dataKey={workcell.dataKey}
                  name={workcell.name}
                  stroke={getWorkcellColor(workcell.id, PALETTE, workcellIds)}
                  strokeWidth={activeDataKeys.has(workcell.dataKey) ? 4 : 2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                  hide={hiddenDataKeys.has(workcell.dataKey)}
                  opacity={activeDataKeys.size === 0 || activeDataKeys.has(workcell.dataKey) ? 1 : 0.25}
                />
              </React.Fragment>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1.5fr) repeat(3, minmax(90px, 0.6fr))',
            gap: 2,
            px: 2,
            py: 1.5,
            bgcolor: 'action.hover',
            fontWeight: 700,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("monthly.oeeScheduler.table.workcell")}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("monthly.oeeScheduler.table.min")}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("monthly.oeeScheduler.table.max")}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("monthly.oeeScheduler.table.avg")}</Typography>
        </Box>

        {legendRows.map((workcell, index) => {
          const color = getWorkcellColor(workcell.id, PALETTE, workcellIds);
          const hidden = hiddenDataKeys.has(workcell.dataKey);
          const active = activeDataKeys.has(workcell.dataKey);

          return (
            <Box
              key={workcell.dataKey}
              onClick={() => handleLegendClick(workcell.dataKey)}
              onDoubleClick={() => handleLegendDoubleClick(workcell.dataKey)}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1.5fr) repeat(3, minmax(90px, 0.6fr))',
                gap: 2,
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                userSelect: 'none',
                borderTop: index === 0 ? 0 : 1,
                borderColor: 'divider',
                bgcolor: active ? `${color}14` : 'background.paper',
                opacity: hidden ? 0.45 : 1,
              }}
              title="Click to focus, double-click to hide/show"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: hidden ? 'line-through' : 'none',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {workcell.name}
                </Typography>
              </Box>
              <Typography variant="body2">{workcell.min != null ? formatPercent01(workcell.min) : t("common.na")}</Typography>
              <Typography variant="body2">{workcell.max != null ? formatPercent01(workcell.max) : t("common.na")}</Typography>
              <Typography variant="body2">{workcell.avg != null ? formatPercent01(workcell.avg) : t("common.na")}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthlyWorkcellOeeScheduler;
